import { useState, type FormEvent } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { toast } from '@mlm/ui/components/toast';
import type { CoachingGoalUpdate } from '@mlm/shared';
import { calendarDaysBetween } from '@mlm/shared/coaching';
import {
  Block,
  CoachButton,
  EmptyCircle,
  ErrorNote,
  Eyebrow,
  Field,
  GoldSeal,
  inputClass,
  Loading,
  SageCheck,
  Screen,
} from '../components/primitives';
import { coaching, type Milestone } from '../lib/api';
import { longDay } from '../lib/format';
import { qk, useRefreshCoaching, useToday } from '../lib/queries';

/** SC-34. */
export function GoalDetailPage() {
  const { id = '' } = useParams();
  const goal = useQuery({
    queryKey: qk.goal(id),
    queryFn: () => coaching.goal(id),
    enabled: Boolean(id),
  });
  const today = useToday();
  const refresh = useRefreshCoaching();
  const [progress, setProgress] = useState<number | null>(null);
  const [milestone, setMilestone] = useState('');

  const update = useMutation({
    mutationFn: (patch: CoachingGoalUpdate) => coaching.updateGoal(id, patch),
    onSuccess: (g) => {
      setProgress(null);
      if (g.status === 'achieved') toast.success('Goal achieved. Take a moment to recognize it.');
      void refresh();
    },
  });
  const toggleMilestone = useMutation({
    mutationFn: (m: Milestone) =>
      coaching.updateMilestone(m.id, { status: m.status === 'done' ? 'pending' : 'done' }),
    onSuccess: (m) => {
      if (m.status === 'done')
        toast.success('Milestone reached. Take a moment to recognize your progress.');
      void refresh();
    },
  });
  const addMilestone = useMutation({
    mutationFn: () => coaching.addMilestone(id, { title: milestone.trim() }),
    onSuccess: () => {
      setMilestone('');
      void refresh();
    },
  });
  const removeMilestone = useMutation({
    mutationFn: coaching.deleteMilestone,
    onSuccess: () => void refresh(),
  });

  if (goal.isPending) return <Loading />;
  if (goal.isError) return <ErrorNote error={goal.error} retry={() => void goal.refetch()} />;
  const g = goal.data;
  const monthsLeft =
    g.targetDate && today.data
      ? Math.max(0, Math.round(calendarDaysBetween(today.data.day, g.targetDate) / 30.44))
      : null;
  const linkedActions = today.data?.actions.filter((a) => a.goalId === g.id) ?? [];
  const onAddMilestone = (e: FormEvent) => {
    e.preventDefault();
    if (milestone.trim().length >= 2) addMilestone.mutate();
  };

  return (
    <Screen back="/achieve/goals" eyebrow={g.isPrimary ? 'Primary goal' : 'Goal'} title={g.title}>
      {g.status === 'achieved' && (
        <div className="flex items-center gap-3 border-b border-border px-5 py-4 text-sm text-ink">
          <GoldSeal /> Goal achieved
          {g.achievedAt ? ` on ${longDay(g.achievedAt.slice(0, 10))}` : ''}
        </div>
      )}
      <section className="grid grid-cols-2 gap-4 border-b border-border px-5 py-6">
        <div>
          <Eyebrow>Progress</Eyebrow>
          <p className="mt-1 font-display text-4xl font-semibold text-ink">{g.progressPercent}%</p>
        </div>
        <div>
          <Eyebrow>Time remaining</Eyebrow>
          <p className="mt-1 font-display text-4xl font-semibold text-ink">
            {monthsLeft === null ? (
              <span className="font-sans text-base font-normal text-muted-foreground">
                No target date
              </span>
            ) : (
              `${monthsLeft} mo`
            )}
          </p>
          {g.targetDate && <p className="eyebrow mt-1">{longDay(g.targetDate)}</p>}
        </div>
      </section>
      {g.reasonWhy && <Block eyebrow="Why this matters">{g.reasonWhy}</Block>}

      <section className="border-b border-border">
        <Eyebrow className="px-5 pb-2 pt-6">Milestones</Eyebrow>
        {g.milestones.length === 0 && (
          <p className="px-5 pb-4 text-sm text-muted-foreground">
            Break this goal into a few milestones.
          </p>
        )}
        {g.milestones.map((m) => (
          <div key={m.id} className="flex items-start gap-3.5 border-t border-border px-5 py-4">
            <button
              type="button"
              className="mt-0.5 shrink-0"
              aria-label={
                m.status === 'done' ? `Mark "${m.title}" as not done` : `Mark "${m.title}" as done`
              }
              onClick={() => toggleMilestone.mutate(m)}
            >
              {m.status === 'done' ? <SageCheck /> : <EmptyCircle />}
            </button>
            <div className="min-w-0 flex-1">
              <p
                className={
                  m.status === 'done'
                    ? 'text-[0.95rem] text-ink/60 line-through'
                    : 'text-[0.95rem] text-ink'
                }
              >
                {m.title}
              </p>
              {m.targetDate && <p className="eyebrow mt-1">Target {longDay(m.targetDate)}</p>}
            </div>
            <CoachButton
              kind="quiet"
              onClick={() => removeMilestone.mutate(m.id)}
              aria-label={`Remove "${m.title}"`}
            >
              Remove
            </CoachButton>
          </div>
        ))}
        <form onSubmit={onAddMilestone} className="flex gap-2 border-t border-border px-5 py-4">
          <input
            className={inputClass}
            placeholder="Add a milestone"
            value={milestone}
            onChange={(e) => setMilestone(e.target.value)}
            maxLength={200}
            aria-label="New milestone"
          />
          <CoachButton
            type="submit"
            disabled={milestone.trim().length < 2 || addMilestone.isPending}
          >
            Add
          </CoachButton>
        </form>
      </section>

      <section className="border-b border-border px-5 py-6">
        <Eyebrow className="mb-3">Today's actions for this goal</Eyebrow>
        {linkedActions.length === 0 ? (
          <p className="text-sm text-muted-foreground">No actions for this goal today.</p>
        ) : (
          <ul className="space-y-2">
            {linkedActions.map((a) => (
              <li key={a.id} className="flex items-center gap-2.5 text-[0.95rem] text-ink">
                {a.status === 'done' ? <SageCheck size={18} /> : <EmptyCircle size={18} />}{' '}
                {a.title}
              </li>
            ))}
          </ul>
        )}
        <Link
          to="/achieve/actions"
          className="eyebrow mt-4 inline-block underline underline-offset-4 hover:text-ink"
        >
          Plan actions
        </Link>
      </section>

      {g.status === 'active' && (
        <section className="space-y-3 border-b border-border px-5 py-6">
          <Field label="Update progress" htmlFor="progress">
            <div className="flex items-center gap-4">
              <input
                id="progress"
                type="range"
                min={0}
                max={100}
                step={5}
                value={progress ?? g.progressPercent}
                onChange={(e) => setProgress(Number(e.target.value))}
                className="h-11 flex-1 accent-[#277A0B]"
              />
              <span className="w-12 text-right font-mono text-ink">
                {progress ?? g.progressPercent}%
              </span>
            </div>
          </Field>
          <CoachButton
            disabled={progress === null || progress === g.progressPercent || update.isPending}
            onClick={() => progress !== null && update.mutate({ progressPercent: progress })}
          >
            Save progress
          </CoachButton>
        </section>
      )}

      <section className="flex flex-wrap items-center gap-3 px-5 py-6">
        {g.status === 'active' && !g.isPrimary && (
          <CoachButton onClick={() => update.mutate({ isPrimary: true })}>Make primary</CoachButton>
        )}
        {g.status === 'active' && (
          <CoachButton onClick={() => update.mutate({ status: 'achieved' })}>
            Mark achieved
          </CoachButton>
        )}
        {g.status === 'archived' ? (
          <CoachButton onClick={() => update.mutate({ status: 'active' })}>
            Restore goal
          </CoachButton>
        ) : (
          <CoachButton kind="quiet" onClick={() => update.mutate({ status: 'archived' })}>
            Archive
          </CoachButton>
        )}
      </section>
    </Screen>
  );
}
